import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Mail, Pencil, Plus, Send, Trash2, Users } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { WORK_MODES, api, type DepartmentRef, type EmployeeSalary, type Paginated } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { enumLabel, useI18n, type TranslationKey } from '../lib/i18n';
import { formatDate, formatDateTime, formatMoney } from '../lib/format';
import {
  Badge,
  type BadgeTone,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  Detail,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingBlock,
  PageHeader,
  Pagination,
  Select,
  StatusDot,
  Table,
  Tabs,
  Td,
  Th,
  Tr,
  useToast,
} from '../components/ui';
import { WorkModeLabel } from '../components/WorkModeLabel';

/** Administrator stands out; an extra permission beside the role (Create tickets) reads as one. */
const roleTone = (role: { code: string; addOn?: boolean }): BadgeTone =>
  role.code === 'ADMIN' ? 'brand' : role.addOn ? 'info' : 'neutral';

export function EmployeesPage() {
  const { t, locale, localized } = useI18n();
  const { can, user } = useAuth();
  // As the API's seesPrivateProfile: a colleague's last sign-in comes only to these.
  const seesSignIns = can(P.USERS_VIEW_ALL) || can(P.USERS_MANAGE) || can(P.ATTENDANCE_MANAGE);
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, string>>({});

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['users', page, filters],
    queryFn: () => api.get<Paginated<any>>('/users', { page, pageSize: 25, ...filters }),
  });

  const { data: departments } = useQuery({
    queryKey: ['departments', 'options'],
    queryFn: () => api.get<Paginated<DepartmentRef>>('/departments', { pageSize: 100 }),
  });

  const { data: roles } = useQuery({
    queryKey: ['roles'],
    queryFn: () => api.get<any[]>('/roles'),
  });

  return (
    <>
      <PageHeader
        title={t('employees.title')}
        // Everyone but the administrators sees the people in their own departments (2026-10-07).
        subtitle={
          can(P.USERS_VIEW_ALL) ? undefined : t(user?.departments.length ? 'reach.employeesSubtitle' : 'reach.noDepartment')
        }
        count={data?.total}
        action={
          can(P.USERS_MANAGE) && (
            <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/employees/new')}>
              {t('employees.new')}
            </Button>
          )
        }
      />

      <Card className="mb-4" padded={false}>
        <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
          <Input
            placeholder={t('common.search')}
            value={filters.q ?? ''}
            onChange={(e) => {
              setFilters({ ...filters, q: e.target.value });
              setPage(1);
            }}
          />
          <Select value={filters.departmentId ?? ''} onChange={(e) => setFilters({ ...filters, departmentId: e.target.value })}>
            <option value="">{t('common.department')} — {t('common.all')}</option>
            {departments?.items.map((d) => (
              <option key={d.id} value={d.id}>{localized(d)}</option>
            ))}
          </Select>
          <Select value={filters.roleCode ?? ''} onChange={(e) => setFilters({ ...filters, roleCode: e.target.value })}>
            <option value="">{t('common.role')} — {t('common.all')}</option>
            {roles?.filter((r) => !r.addOn).map((r) => (
              <option key={r.id} value={r.code}>{locale === 'ar' ? r.nameAr ?? r.name : r.name}</option>
            ))}
            {/* Who holds an extra permission — who may create tickets, say. */}
            {roles?.some((r) => r.addOn) && (
              <optgroup label={t('employees.extraPermissions')}>
                {roles.filter((r) => r.addOn).map((r) => (
                  <option key={r.id} value={r.code}>{locale === 'ar' ? r.nameAr ?? r.name : r.name}</option>
                ))}
              </optgroup>
            )}
          </Select>
          {/* Only those at the office, or only those remote (2026-10-21). */}
          <Select
            value={filters.workMode ?? ''}
            onChange={(e) => {
              setFilters({ ...filters, workMode: e.target.value });
              setPage(1);
            }}
          >
            <option value="">{t('employees.workMode')} — {t('common.all')}</option>
            {WORK_MODES.map((mode) => (
              <option key={mode} value={mode}>{t(`employees.workMode.${mode}`)}</option>
            ))}
          </Select>
        </div>
      </Card>

      <Card padded={false}>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.items.length ? (
          <EmptyState icon={<Users className="h-8 w-8" />} />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>{t('common.name')}</Th>
                  <Th>{t('common.email')}</Th>
                  <Th>{t('common.department')}</Th>
                  <Th>{t('common.role')}</Th>
                  <Th>{t('employees.workMode')}</Th>
                  <Th>{t('common.status')}</Th>
                  {seesSignIns && <Th align="end">{t('employees.lastSignIn')}</Th>}
                </tr>
              </thead>
              <tbody>
                {data.items.map((u) => (
                  <Tr key={u.id} onClick={() => navigate(`/employees/${u.id}`)}>
                    <Td>
                      <span className="block">{localized(u)}</span>
                      {u.employeeCode && (
                        <span className="block font-mono text-caption text-faint">{u.employeeCode}</span>
                      )}
                    </Td>
                    <Td className="text-muted" dir="ltr">{u.email}</Td>
                    <Td className="text-muted">
                      {u.departments.map((d: any) => localized(d)).join(', ') || '—'}
                    </Td>
                    <Td>
                      <span className="flex flex-wrap gap-1">
                        {u.roles.map((r: any) => (
                          <Badge key={r.code} tone={roleTone(r)}>
                            {locale === 'ar' ? r.nameAr ?? r.name : r.name}
                          </Badge>
                        ))}
                      </span>
                    </Td>
                    <Td>
                      <WorkModeLabel mode={u.workMode} className="text-muted" />
                    </Td>
                    <Td>
                      <span className="flex items-center gap-2">
                        <StatusDot tone={u.isActive ? 'online' : 'offline'} />
                        <span className="text-muted">{u.isActive ? t('employees.active') : t('employees.inactive')}</span>
                      </span>
                    </Td>
                    {seesSignIns && (
                      <Td align="end" className="text-caption text-muted">
                        {u.lastLoginAt ? formatDateTime(u.lastLoginAt, locale) : t('common.never')}
                      </Td>
                    )}
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={setPage} />
          </>
        )}
      </Card>

    </>
  );
}

export function EmployeeDetailPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const { can, user } = useAuth();
  // A department's page opens for its own people, or for whoever sees every one (the API's seesDepartment).
  const opensDepartment = (departmentId: string) =>
    can(P.USERS_VIEW_ALL) || !!user?.departments.some((d) => d.id === departmentId);
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('profile');

  const { data: employee, isLoading, error, refetch } = useQuery({
    queryKey: ['user', id],
    queryFn: () => api.get<any>(`/users/${id}`),
  });

  const { data: shifts } = useQuery({
    queryKey: ['user', id, 'shifts'],
    queryFn: () => api.get<any[]>(`/users/${id}/shifts`),
    enabled: tab === 'shifts',
  });

  const setActive = useMutation({
    mutationFn: (active: boolean) => api.post(`/users/${id}/${active ? 'activate' : 'deactivate'}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['user', id] }),
    onError: (e: any) => toast.error(e.message),
  });

  // Someone who has never signed in can be sent their access email again, with a new password.
  const [confirmResend, setConfirmResend] = useState(false);
  const resendAccess = useMutation({
    mutationFn: () => api.post<{ welcomeEmail: { to: string } }>(`/users/${id}/resend-access`),
    onSuccess: (sent) => toast.success(t('employees.resendAccessSent', { email: sent.welcomeEmail.to })),
    onError: (e: any) => {
      const reason = e?.code === 'ACCESS_EMAIL_NOT_SENT' ? RESEND_REFUSED[e?.details?.reason as string] : undefined;
      toast.error(reason ? t(reason) : e.message);
      // Someone who signed in meanwhile, or was switched off, now reads as they are.
      void queryClient.invalidateQueries({ queryKey: ['user', id] });
    },
    onSettled: () => setConfirmResend(false),
  });

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  // Only someone whose Finance roles handle payroll is sent this at all.
  const salary = employee.salary as EmployeeSalary | undefined;

  return (
    <>
      <div className="mb-4">
        <Link to="/employees" className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]">
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {t('employees.title')}
        </Link>
      </div>

      <PageHeader
        title={localized(employee)}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-muted" dir="ltr">{employee.email}</span>
            {employee.roles.map((r: any) => (
              <Badge key={r.code} tone={roleTone(r)}>
                {locale === 'ar' ? r.nameAr ?? r.name : r.name}
              </Badge>
            ))}
          </span>
        }
        action={
          <div className="flex flex-wrap gap-2">
            {/* Anyone may mail any other employee (operator, 2026-10-09). */}
            {employee.isActive && employee.id !== user?.id && (
              <Button icon={<Mail className="h-4 w-4" />} onClick={() => navigate(`/mail/new?to=${employee.id}`)}>
                {t('mail.sendMail')}
              </Button>
            )}
            {can(P.USERS_MANAGE) && (
              <>
                <Button icon={<Pencil className="h-4 w-4" />} onClick={() => navigate(`/employees/${id}/edit`)}>
                  {t('common.edit')}
                </Button>
                <Button
                  variant={employee.isActive ? 'danger' : 'primary'}
                  onClick={() => setActive.mutate(!employee.isActive)}
                  loading={setActive.isPending}
                >
                  {employee.isActive ? t('employees.deactivate') : t('employees.activate')}
                </Button>
              </>
            )}
            {/* For good, with everything that is theirs: the Administrator's alone, and never oneself. */}
            {can(P.USERS_DELETE) && employee.id !== user?.id && (
              <Button
                variant="danger"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={() => navigate(`/employees/${id}/delete`)}
              >
                {t('employees.delete')}
              </Button>
            )}
          </div>
        }
      />

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'profile', label: t('auth.myProfile') },
          { id: 'shifts', label: t('nav.shifts') },
          // The salary tab moved to sys, with payroll.
        ]}
      />

      <div className="mt-5">
        {tab === 'profile' && (
          <div className="grid gap-5 lg:grid-cols-2">
            <Card>
              <CardHeader title={t('auth.myProfile')} />
              <dl className="divide-y divide-[var(--line)]">
                <Detail label={t('common.name')}>{employee.fullName}</Detail>
                <Detail label={`${t('common.name')} (AR)`}>{employee.fullNameAr ?? '—'}</Detail>
                <Detail label={t('common.email')}><span dir="ltr">{employee.email}</span></Detail>
                <Detail label={t('employees.code')}>{employee.employeeCode ?? '—'}</Detail>
                <Detail label={t('employees.jobTitle')}>{employee.jobTitle ?? '—'}</Detail>
                <Detail label={t('employees.workMode')}>
                  <WorkModeLabel mode={employee.workMode} />
                </Detail>
                <Detail label={t('common.phone')}><span dir="ltr">{employee.phone ?? '—'}</span></Detail>
                <Detail label={t('employees.language')}>{employee.locale === 'ar' ? 'العربية' : 'English'}</Detail>
                <Detail label={t('common.status')}>
                  <span className="inline-flex items-center gap-2">
                    <StatusDot tone={employee.isActive ? 'online' : 'offline'} />
                    {employee.isActive ? t('employees.active') : t('employees.inactive')}
                  </span>
                </Detail>
                {/* A colleague's last sign-in and Finance access come only to those the API sends them to. */}
                {'lastLoginAt' in employee && (
                  <Detail label={t('employees.lastSignIn')}>
                    {employee.lastLoginAt ? (
                      formatDateTime(employee.lastLoginAt, locale)
                    ) : (
                      <span className="inline-flex flex-wrap items-baseline justify-end gap-x-3 gap-y-1">
                        <span>{t('common.never')}</span>
                        {can(P.USERS_MANAGE) && employee.isActive && (
                          <button
                            type="button"
                            onClick={() => setConfirmResend(true)}
                            className="inline-flex items-center gap-1 text-body-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
                          >
                            <Send className="h-3.5 w-3.5 flip-rtl self-center" aria-hidden />
                            {t('employees.resendAccess')}
                          </button>
                        )}
                      </span>
                    )}
                  </Detail>
                )}
                {salary && (
                  <Detail label={t('employees.basicSalary')}>
                    <span className="block">
                      {salary.current ? (
                        <>
                          <span className="tabular">
                            {formatMoney(salary.current.basicSalary, salary.current.currency, locale)}
                          </span>{' '}
                          <span className="text-caption text-muted">
                            {t('employees.salarySince', { date: formatDate(salary.current.effectiveFrom, locale) })}
                          </span>
                        </>
                      ) : (
                        <Badge tone="warning">{t('employees.salaryNone')}</Badge>
                      )}
                    </span>
                    {salary.scheduled && (
                      <span className="block text-caption text-muted">
                        {t('employees.salaryNext', {
                          amount: formatMoney(salary.scheduled.basicSalary, salary.scheduled.currency, locale),
                          date: formatDate(salary.scheduled.effectiveFrom, locale),
                        })}
                      </span>
                    )}
                  </Detail>
                )}
                {/* Read-only here: sys access is given and taken away in sys. */}
                {'sysRoles' in employee && (
                  <Detail label={t('employees.sysAccess')}>
                    {employee.sysRoles?.length ? (
                      <span className="inline-flex flex-wrap justify-end gap-1.5">
                        {employee.sysRoles.map((r: any) => (
                          <Badge key={r.code} tone="brand">
                            {locale === 'ar' ? r.nameAr ?? r.name : r.name}
                          </Badge>
                        ))}
                      </span>
                    ) : (
                      <span className="text-faint">{t('employees.noSysAccess')}</span>
                    )}
                  </Detail>
                )}
              </dl>
            </Card>

            <Card>
              <CardHeader title={t('departments.title')} />
              {employee.departments.length ? (
                <ul className="space-y-2">
                  {employee.departments.map((d: any) => (
                    <li key={d.id} className="flex items-center justify-between gap-3 rounded-md border hairline px-3 py-2">
                      {opensDepartment(d.id) ? (
                        <Link to={`/departments/${d.id}`} className="min-w-0 truncate hover:text-primary-600 dark:hover:text-primary-400">
                          <span className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">{d.code}</span>{' '}
                          {localized(d)}
                        </Link>
                      ) : (
                        <span className="min-w-0 truncate">
                          <span className="font-mono text-mono-sm text-muted">{d.code}</span> {localized(d)}
                        </span>
                      )}
                      <Badge tone={d.memberType === 'LEADER' ? 'brand' : 'neutral'}>
                        {enumLabel(d.memberType, locale)}
                      </Badge>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-body-sm text-muted">{t('common.none')}</p>
              )}
            </Card>
          </div>
        )}

        {tab === 'shifts' && (
          <Card padded={false}>
            {!shifts?.length ? (
              <EmptyState />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>{t('common.date')}</Th>
                    <Th>{t('shifts.definitions')}</Th>
                    <Th align="end">{t('common.notes')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {shifts.map((s) => (
                    <Tr key={s.id}>
                      <Td className="tabular">{formatDate(s.date, locale)}</Td>
                      <Td><Badge tone="neutral">{enumLabel(s.type, locale)}</Badge></Td>
                      <Td align="end" className="text-caption text-faint">{s.notes ?? '—'}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        )}

      </div>

      {confirmResend && (
        <ConfirmDialog
          title={t('employees.resendAccessConfirm')}
          confirmLabel={t('employees.resendAccess')}
          busy={resendAccess.isPending}
          onConfirm={() => resendAccess.mutate()}
          onCancel={() => setConfirmResend(false)}
        >
          <p>{t('employees.resendAccessBody', { name: localized(employee) })}</p>
          <p>
            <span dir="ltr">{employee.email}</span>
          </p>
        </ConfirmDialog>
      )}
    </>
  );
}

/** What the admin says when the API kept the password and sent nothing (`details.reason`). */
const RESEND_REFUSED: Record<string, TranslationKey> = {
  SIGNED_IN: 'employees.resendAccessSignedIn',
  INACTIVE: 'employees.resendAccessInactive',
  EMAIL_OFF: 'employees.resendAccessOff',
  EMAIL_NOT_CONFIGURED: 'employees.resendAccessNotConfigured',
};
